import React from 'react'
import { supabase } from '@/lib/supabase'
import { fetchCommunitySummary, type CommunitySummaryMap } from '../services/communityService'

export function useCommunitySummary(opportunityIds: number[], enabled: boolean | null) {
  const [loading, setLoading] = React.useState(false)
  const [map, setMap] = React.useState<CommunitySummaryMap>({})

  const ids = React.useMemo(
    () =>
      Array.from(
        new Set(
          (opportunityIds || [])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id)),
        ),
      ).sort((a, b) => a - b),
    [opportunityIds],
  )

  const idKey = React.useMemo(() => ids.join(','), [ids])

  const load = React.useCallback(
    async (targetIds: number[] | null = null) => {
      if (!enabled) {
        setMap({})
        return
      }
      const effectiveIds = (targetIds && targetIds.length > 0 ? targetIds : ids) || []
      if (!effectiveIds.length) {
        setMap({})
        return
      }

      setLoading(true)
      try {
        const result = await fetchCommunitySummary(effectiveIds)
        setMap(result)
      } catch {
        setMap({})
      } finally {
        setLoading(false)
      }
    },
    [enabled, ids],
  )

  React.useEffect(() => {
    if (!enabled) {
      setMap({})
      return
    }
    if (!ids.length) {
      setMap({})
      return
    }

    let active = true

    ;(async () => {
      await load(ids)
      if (!active) return
    })()

    return () => {
      active = false
    }
  }, [enabled, ids, load, idKey])

  React.useEffect(() => {
    if (!enabled || !ids.length) return

    const idsSet = new Set(ids)
    let reloadTimer: ReturnType<typeof setTimeout> | null = null
    let interval: ReturnType<typeof setInterval> | null = null

    const scheduleReload = () => {
      if (reloadTimer) clearTimeout(reloadTimer)
      reloadTimer = setTimeout(() => {
        load(ids).catch(() => {
          // silencioso; próximo evento corrige
        })
      }, 400)
    }

    const channel = supabase
      .channel(`community_summary:${idKey}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'opportunity_community_messages',
        } as any,
        (payload: any) => {
          const row = (payload.new || payload.old) as { opportunity_id?: number }
          const oppId = Number(row?.opportunity_id)
          if (Number.isFinite(oppId) && idsSet.has(oppId)) {
            scheduleReload()
          }
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'opportunity_community_reactions',
        } as any,
        (payload: any) => {
          const row = (payload.new || payload.old) as { opportunity_id?: number }
          const oppId = Number(row?.opportunity_id)
          if (Number.isFinite(oppId) && idsSet.has(oppId)) {
            scheduleReload()
          }
        },
      )
      .subscribe()

    // Fallback: polling leve para garantir que o resumo (avatars,
    // quantidade de respostas e última resposta) seja atualizado
    // mesmo se o canal realtime falhar.
    interval = setInterval(() => {
      load(ids).catch(() => {
        // silencioso; próximo ciclo corrige
      })
    }, 3500)

  return () => {
      try {
        supabase.removeChannel(channel)
      } catch {
        // noop
      }
      if (reloadTimer) {
        clearTimeout(reloadTimer)
      }
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [enabled, idKey, ids, load])

  return { loading, map }
}
